/**
 * @file Cibles decoratives du hub generees par donnees : decor de saison (printemps / ete / automne / hiver), arbres geants (on passe dessous,
 * a l'ombre) et herbe a chat. Toutes non tirables, non destructibles. Les ids suivent `hd_<saison>_<cle>` (voir HUB_SEASONS).
 */
import { def } from './targetDef';
import type { HubSeason } from './hub';
import type { LocalizedName, TargetDef, TargetPalette, TargetThemeId } from './types';

const BIOMES: TargetDef['biomes'] = ['forest', 'desert', 'tundra'];

interface Small {
  key: string;
  theme: TargetThemeId;
  name: LocalizedName;
  palette: TargetPalette;
  scale: number;
  radius: number;
  solid: boolean;
  variants?: number;
}

interface Giant {
  key: string;
  theme: 'giantOak' | 'giantPine' | 'giantWillow' | 'giantPalm' | 'giantBaobab';
  name: LocalizedName;
  palette: TargetPalette;
  /** Echelle (un arbre normal ~ 1.1 ; un geant 3 a 4). */
  scale: number;
  /** Rayon du tronc (collider solide) et de la couronne, en unites du constructeur (avant echelle). */
  trunk: number;
  canopy: number;
}

const TRUNK = '#9a6b45';

const small = (season: HubSeason, s: Small): TargetDef =>
  def({
    id: `hd_${season}_${s.key}`,
    family: 'decor',
    name: s.name,
    visual: { theme: s.theme, palette: s.palette, scale: s.scale, variants: s.variants ?? 3 },
    shootable: false,
    destructible: false,
    solid: s.solid,
    colliderRadius: s.radius,
    biomes: BIOMES,
    rarity: { tier: 'common', weight: 0 },
  });

const giant = (season: HubSeason, g: Giant): TargetDef =>
  def({
    id: `hd_${season}_${g.key}`,
    family: 'decor',
    name: g.name,
    visual: { theme: g.theme, palette: g.palette, scale: g.scale, variants: 3, canopyRadius: g.canopy },
    shootable: false,
    destructible: false,
    solid: true,
    colliderRadius: g.trunk,
    biomes: BIOMES,
    rarity: { tier: 'common', weight: 0 },
  });

const pal = (main: string, secondary: string, accent: string, trunk: string = TRUNK): TargetPalette => ({ main, secondary, accent, trunk });
const tree = (key: string, fr: string, en: string, palette: TargetPalette, theme: TargetThemeId = 'oak'): Small => ({ key, theme, name: { fr, en }, palette, scale: 1.1, radius: 0.8, solid: true, variants: 4 });
const bush = (palette: TargetPalette): Small => ({ key: 'bush', theme: 'bush', name: { fr: 'Buisson', en: 'Bush' }, palette, scale: 0.75, radius: 0.5, solid: false });
const rock = (palette: TargetPalette): Small => ({ key: 'rock', theme: 'rockSmall', name: { fr: 'Rocher', en: 'Rock' }, palette, scale: 0.8, radius: 0.7, solid: true });
const flowers = (palette: TargetPalette): Small => ({ key: 'flowers', theme: 'flowers', name: { fr: 'Parterre de fleurs', en: 'Flower bed' }, palette, scale: 1.5, radius: 0.4, solid: false, variants: 4 });

const SEASON_DEFS: ReadonlyArray<{ season: HubSeason; smalls: Small[]; giants: Giant[] }> = [
  {
    season: 'spring',
    smalls: [
      tree('oak', 'Chene printanier', 'Spring oak', pal('#a8e58f', '#86d17d', '#d2f7a6')),
      tree('blossom', 'Cerisier en fleurs', 'Blossom tree', pal('#ffc2da', '#ff9fc2', '#ffe6f0', '#9c6b57')),
      tree('blossomWhite', 'Prunier blanc', 'White plum', pal('#fff0f5', '#f4cde0', '#ffffff', '#9c6b57')),
      bush(pal('#ffb8d2', '#8fd68a', '#fff0f6')),
      rock(pal('#cfd8cf', '#b0bdb1', '#e9efe9')),
      flowers(pal('#ff9fc3', '#ffe27a', '#ffffff')),
    ],
    giants: [
      { key: 'giantOak', theme: 'giantOak', name: { fr: 'Grand chene', en: 'Giant oak' }, palette: pal('#9fe08a', '#7cc97a', '#c9f2a0'), scale: 3.2, trunk: 0.2, canopy: 1.25 },
      { key: 'giantBlossom', theme: 'giantOak', name: { fr: 'Grand cerisier', en: 'Giant cherry' }, palette: pal('#ffc0d8', '#ff9fc3', '#ffe3ee', '#8a5a4a'), scale: 3.4, trunk: 0.2, canopy: 1.3 },
      { key: 'giantWillow', theme: 'giantWillow', name: { fr: 'Saule pleureur', en: 'Weeping willow' }, palette: pal('#b4e89a', '#8fd47e', '#d8f7b8'), scale: 3.2, trunk: 0.2, canopy: 1.3 },
      { key: 'giantPine', theme: 'giantPine', name: { fr: 'Grand pin', en: 'Giant pine' }, palette: pal('#5fbf8a', '#3f9e72', '#9ee0b8'), scale: 3.6, trunk: 0.16, canopy: 0.9 },
    ],
  },
  {
    season: 'summer',
    smalls: [
      tree('oak', 'Chene', 'Oak', pal('#7fd374', '#5bba66', '#b3ea86')),
      tree('palm', 'Palmier', 'Palm', pal('#52c46a', '#3aa656', '#9be28a'), 'palm'),
      bush(pal('#8ed886', '#6ec174', '#c8f0a0')),
      rock(pal('#cfd5de', '#aeb7c4', '#eceff4')),
      flowers(pal('#ffd23c', '#ff6b6b', '#ffffff')),
    ],
    giants: [
      { key: 'giantOak', theme: 'giantOak', name: { fr: 'Grand chene', en: 'Giant oak' }, palette: pal('#5fc25f', '#44a84f', '#9be07e'), scale: 3.3, trunk: 0.2, canopy: 1.25 },
      { key: 'giantPalm', theme: 'giantPalm', name: { fr: 'Grand palmier', en: 'Giant palm' }, palette: pal('#4fc26a', '#35a354', '#9be28a', '#b48a5a'), scale: 3.4, trunk: 0.14, canopy: 1.1 },
      { key: 'giantWillow', theme: 'giantWillow', name: { fr: 'Saule pleureur', en: 'Weeping willow' }, palette: pal('#8fd96f', '#6cc460', '#c2f09a'), scale: 3.2, trunk: 0.2, canopy: 1.3 },
      { key: 'giantBaobab', theme: 'giantBaobab', name: { fr: 'Baobab', en: 'Baobab' }, palette: pal('#8fcf6a', '#6fb85a', '#c2e89a', '#b08a62'), scale: 3.0, trunk: 0.34, canopy: 1.2 },
      { key: 'giantPine', theme: 'giantPine', name: { fr: 'Grand pin', en: 'Giant pine' }, palette: pal('#3f9e6c', '#2d835a', '#86d1a4'), scale: 3.6, trunk: 0.16, canopy: 0.9 },
    ],
  },
  {
    season: 'autumn',
    smalls: [
      tree('maple', 'Erable', 'Maple', pal('#ff9a4d', '#e87a32', '#ffc98a', '#8f5a3a')),
      tree('oakRed', 'Chene rouge', 'Red oak', pal('#e0644e', '#c24a3c', '#ff9f8a', '#7d4b36')),
      tree('larch', 'Meleze dore', 'Golden larch', pal('#c9ae4e', '#a38a37', '#ead67f'), 'fir'),
      bush(pal('#e8a24a', '#c9792f', '#ffd08a')),
      rock(pal('#c9c0b4', '#a79c8e', '#e6dfd4')),
      { key: 'pumpkin', theme: 'pumpkin', name: { fr: 'Citrouille', en: 'Pumpkin' }, palette: pal('#ff9a2e', '#e87a1c', '#6fae4a'), scale: 1.0, radius: 0.55, solid: true },
      { key: 'leaves', theme: 'leaves', name: { fr: 'Tas de feuilles', en: 'Leaf pile' }, palette: pal('#e8883a', '#c9602f', '#f2c14e'), scale: 1.2, radius: 0.5, solid: false, variants: 4 },
    ],
    giants: [
      { key: 'giantOak', theme: 'giantOak', name: { fr: 'Grand erable', en: 'Giant maple' }, palette: pal('#ff9a4d', '#e87a32', '#ffc98a', '#7d4b36'), scale: 3.3, trunk: 0.2, canopy: 1.25 },
      { key: 'giantRed', theme: 'giantOak', name: { fr: 'Grand chene rouge', en: 'Giant red oak' }, palette: pal('#e0644e', '#c24a3c', '#ff9f8a', '#6d3f2e'), scale: 3.3, trunk: 0.2, canopy: 1.25 },
      { key: 'giantWillow', theme: 'giantWillow', name: { fr: 'Saule dore', en: 'Golden willow' }, palette: pal('#e8c24e', '#d0a23a', '#f6e08a'), scale: 3.2, trunk: 0.2, canopy: 1.3 },
      { key: 'giantPine', theme: 'giantPine', name: { fr: 'Grand meleze', en: 'Giant larch' }, palette: pal('#c9ae4e', '#a38a37', '#ead67f'), scale: 3.6, trunk: 0.16, canopy: 0.9 },
    ],
  },
  {
    season: 'winter',
    smalls: [
      tree('fir', 'Sapin enneige', 'Snowy fir', pal('#e9f4fb', '#bcd8ea', '#ffffff', '#8f6a45'), 'fir'),
      tree('oakFrost', 'Chene givre', 'Frosted oak', pal('#d3eaf6', '#a9cfe5', '#ffffff', '#8c7a6a')),
      bush(pal('#f2f8fc', '#cfe3ef', '#ffffff')),
      rock(pal('#e4ecf2', '#c3d1dc', '#ffffff')),
      { key: 'snowman', theme: 'snowman', name: { fr: 'Bonhomme de neige', en: 'Snowman' }, palette: pal('#ffffff', '#dcecf6', '#ff8a3c'), scale: 0.9, radius: 0.6, solid: true },
    ],
    giants: [
      { key: 'giantPine', theme: 'giantPine', name: { fr: 'Grand sapin', en: 'Giant fir' }, palette: pal('#e9f4fb', '#b6d3e6', '#ffffff', '#7d5f45'), scale: 3.7, trunk: 0.16, canopy: 0.9 },
      { key: 'giantOak', theme: 'giantOak', name: { fr: 'Grand chene givre', en: 'Giant frosted oak' }, palette: pal('#d3eaf6', '#a9cfe5', '#ffffff', '#7d6a5a'), scale: 3.3, trunk: 0.2, canopy: 1.25 },
      { key: 'giantWillow', theme: 'giantWillow', name: { fr: 'Saule givre', en: 'Frosted willow' }, palette: pal('#d8ecf7', '#b0d2e6', '#ffffff', '#7d6a5a'), scale: 3.2, trunk: 0.2, canopy: 1.3 },
      { key: 'giantPineBlue', theme: 'giantPine', name: { fr: 'Epicea bleu', en: 'Blue spruce' }, palette: pal('#8fc0e0', '#6a9fc8', '#e4f4ff', '#7d5f45'), scale: 3.6, trunk: 0.16, canopy: 0.9 },
    ],
  },
];

/** Herbe a chat : petite brindille verte a fleurs violettes, non solide (on marche dessus pour la cueillir). */
const CATNIP: TargetDef = def({
  id: 'decor_catnip',
  family: 'decor',
  name: { fr: 'Herbe a chat', en: 'Catnip' },
  visual: { theme: 'catnip', palette: { main: '#7fe08a', secondary: '#4fbf6a', accent: '#b57cff', glow: '#e6d0ff' }, scale: 1.5, variants: 3, sparkle: true },
  shootable: false,
  destructible: false,
  solid: false,
  colliderRadius: 0.5,
  biomes: BIOMES,
  rarity: { tier: 'common', weight: 0 },
});

export const HUB_DECOR_TARGETS: readonly TargetDef[] = [
  ...SEASON_DEFS.flatMap((s) => [...s.smalls.map((x) => small(s.season, x)), ...s.giants.map((g) => giant(s.season, g))]),
  CATNIP,
];
